import React, { useEffect, useMemo, useState } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  getContainerDashboard, getContainers, getContainer, createContainer,
  importContainerAsn, arriveContainer, receiveContainerLine, closeContainer,
  getContainerVarianceReport, getSkus,
  scanCarton, reportDamage, uploadContainerDocument, listContainerDocuments,
  getContainerDocument, deleteContainerDocument, getPutawayPlan,
  executePutaway, cartonBarcodesPdfUrl,
  getLabourKpis, getSupplierScorecard, publishSupplierScorecard,
} from '../lib/api';
import BarcodeScanner from '../components/BarcodeScanner';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { format } from 'date-fns';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from '../components/ui/dialog';
import PoPdfImportDialog from '../components/PoPdfImportDialog';
import LandedCostDialog from '../components/LandedCostDialog';
import ContainerReservationsPanel from '../components/ContainerReservationsPanel';
import useUrlFilterState, { useUrlFilterClear } from '../lib/useUrlFilterState';
import {
  Package, Plus, ShippingContainer, UploadSimple, Truck, Warning, Check,
  ClipboardText, TrendDown, TrendUp, CaretRight, X, Barcode, Camera, FileText,
  Gauge, TreeStructure, Trophy, PaperPlaneTilt, CaretDown, CalendarBlank, Eye,
  Printer, ImageSquare, Lightning, FilePdf, MagnifyingGlass, FunnelSimple, Coins,
} from '@phosphor-icons/react';

const TYPE_LABELS = {
  '10GP': '10 ft', '20GP': '20 ft', '20GT': '20 ft Open',
  '40GP': '40 ft', '40HC': '40 ft HC', 'LCL': 'LCL',
};

const STATUS_STYLES = {
  in_transit: 'bg-blue-100 text-blue-800',
  arrived: 'bg-amber-100 text-amber-800',
  unloading: 'bg-purple-100 text-purple-800 ring-1 ring-purple-300',
  received: 'bg-emerald-100 text-emerald-800',
  closed: 'bg-muted text-foreground',
};

const VARIANCE_STYLES = {
  match: 'text-green-600',
  short: 'text-red-600',
  over: 'text-amber-600',
  over_tolerance: 'text-rose-700 font-bold',
  pending: 'text-muted-foreground',
};

export default function Containers() {
  const { user } = useAuth();
  const isManager = user?.role === 'admin' || user?.role === 'manager';

  const [dashboard, setDashboard] = useState(null);
  const [containers, setContainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useUrlFilterState('status', '');
  // Iter 98 — extra filters + search bar for the list below (URL-backed)
  const [typeFilter, setTypeFilter] = useUrlFilterState('type', '');
  const [supplierFilter, setSupplierFilter] = useUrlFilterState('supplier', '');
  const [etaFilter, setEtaFilter] = useUrlFilterState('eta', ''); // '', 'overdue', '7', '30', 'future'
  const [searchTerm, setSearchTerm] = useUrlFilterState('q', '');
  const clearUrlFilters = useUrlFilterClear(['q', 'status', 'type', 'supplier', 'eta']);

  // Analytics widgets
  const [scorecard, setScorecard] = useState(null);
  const [scorecardOpen, setScorecardOpen] = useState(false);
  const [scorecardBusy, setScorecardBusy] = useState(false);
  const [kpis, setKpis] = useState(null);
  const [kpisOpen, setKpisOpen] = useState(false);

  // Extra dialogs
  const [scannerOpen, setScannerOpen] = useState(false);
  const [damageOpen, setDamageOpen] = useState(false);
  const [damageForm, setDamageForm] = useState({ reason_code: 'DAMAGED', description: '', qty: 0, carton_id: '', photo_base64: '' });
  const [docsOpen, setDocsOpen] = useState(false);
  const [docList, setDocList] = useState([]);
  const [putawayOpen, setPutawayOpen] = useState(false);
  const [putaway, setPutaway] = useState(null);
  const [putawayBusy, setPutawayBusy] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [etaDate, setEtaDate] = useState(null);

  // New container dialog
  const [showCreate, setShowCreate] = useState(false);
  const [showPdfImport, setShowPdfImport] = useState(false);
  const [createMode, setCreateMode] = useState('manual'); // 'manual' | 'asn'
  const [form, setForm] = useState({
    container_number: '', container_type: '40HC', supplier_name: '',
    origin_port: '', eta: '', over_receipt_tolerance_pct: 5, blind_receiving: false,
    // Landed-cost extras (entered when freight invoice is on hand; can also
    // be edited later via PATCH /containers/{id}/costs).
    freight_cost: '', duty_cost: '', insurance_cost: '', other_cost: '',
    cost_allocation_method: 'value',
  });
  const [formLines, setFormLines] = useState([
    { sku_code: '', expected_qty: '', cartons: '', units_per_carton: '', unit_cost: '' },
  ]);
  const [asnFile, setAsnFile] = useState(null);
  const [skus, setSkus] = useState([]);

  // Detail drawer
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [receivingLine, setReceivingLine] = useState(null);
  const [receiveQty, setReceiveQty] = useState('');
  // Iter 126 — landed-cost dialog (opened from container detail).
  const [landedCostFor, setLandedCostFor] = useState(null);

  const refreshAll = async () => {
    setLoading(true);
    try {
      const [d, l] = await Promise.all([
        getContainerDashboard(),
        getContainers(statusFilter ? { status: statusFilter } : {}),
      ]);
      setDashboard(d.data);
      setContainers(l.data);
    } catch (e) {
      toast.error('Failed to load containers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refreshAll(); /* eslint-disable-next-line */ }, [statusFilter]);

  // Derived: client-side filtering for search/type/supplier/ETA on top of the
  // server-side status filter (so users can compose all five without refetching).
  const filteredContainers = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const now = Date.now();
    const dayMs = 24 * 60 * 60 * 1000;
    return containers.filter((c) => {
      if (term && !String(c.container_number || '').toLowerCase().includes(term)) return false;
      if (typeFilter && c.container_type !== typeFilter) return false;
      if (supplierFilter && c.supplier_name !== supplierFilter) return false;
      if (etaFilter) {
        const etaStr = c.eta || c.ata;
        if (etaFilter === 'none') {
          if (etaStr) return false;
        } else if (!etaStr) {
          return false;
        } else {
          const t = new Date(etaStr).getTime();
          if (Number.isNaN(t)) return false;
          const diffDays = (t - now) / dayMs;
          if (etaFilter === 'overdue' && diffDays >= 0) return false;
          if (etaFilter === '7' && (diffDays < 0 || diffDays > 7)) return false;
          if (etaFilter === '30' && (diffDays < 0 || diffDays > 30)) return false;
          if (etaFilter === 'future' && diffDays <= 30) return false;
        }
      }
      return true;
    });
  }, [containers, searchTerm, typeFilter, supplierFilter, etaFilter]);
  useEffect(() => { getSkus().then(r => setSkus(r.data || [])).catch(() => {}); }, []);
  useEffect(() => {
    getSupplierScorecard(90).then(r => setScorecard(r.data)).catch(() => {});
    getLabourKpis(30).then(r => setKpis(r.data)).catch(() => {});
  }, []);

  // Keep form.eta in sync with etaDate picker
  useEffect(() => {
    if (etaDate) {
      setForm(f => ({ ...f, eta: etaDate.toISOString() }));
    }
  }, [etaDate]);

  // Reusable helpers for extra dialogs
  const refreshDetail = async () => {
    if (!detail?.id) return;
    const { data } = await getContainer(detail.id);
    setDetail(data);
  };

  const handleCartonScan = async (decoded) => {
    setScannerOpen(false);
    if (!decoded) return;
    try {
      const { data } = await scanCarton(detail.id, { carton_id: decoded });
      toast.success(`${data.carton_id} · +${data.units_added} units · ${data.variance_status}`);
      await refreshDetail();
      refreshAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Scan failed');
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setDamageForm(f => ({ ...f, photo_base64: ev.target.result }));
    reader.readAsDataURL(file);
  };

  const submitDamage = async () => {
    if (!detail?.id) return;
    try {
      const { data } = await reportDamage(detail.id, damageForm);
      if (damageForm.carton_id && data?.carton_matched === false) {
        toast.warning(`Damage logged — carton ${damageForm.carton_id} not found on this container (report-only)`);
      } else {
        toast.success('Damage logged');
      }
      setDamageOpen(false);
      setDamageForm({ reason_code: 'DAMAGED', description: '', qty: 0, carton_id: '', photo_base64: '' });
      await refreshDetail();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Damage log failed');
    }
  };

  const openDocs = async () => {
    setDocsOpen(true);
    try {
      const { data } = await listContainerDocuments(detail.id);
      setDocList(data);
    } catch (e) {
      toast.error('Failed to load documents');
    }
  };

  const handleDocUpload = async (e, docType) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('File > 5MB'); return; }
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        await uploadContainerDocument(detail.id, {
          doc_type: docType,
          filename: file.name,
          mime_type: file.type,
          content_base64: ev.target.result,
        });
        toast.success('Uploaded');
        const { data } = await listContainerDocuments(detail.id);
        setDocList(data);
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Upload failed');
      }
    };
    reader.readAsDataURL(file);
  };

  const viewDoc = async (docId) => {
    try {
      const { data } = await getContainerDocument(detail.id, docId);
      const win = window.open('', '_blank');
      if (win && data.content_base64) {
        win.document.write(`<iframe src="${data.content_base64}" style="width:100vw;height:100vh;border:0"></iframe>`);
      }
    } catch (e) { toast.error('View failed'); }
  };

  const removeDoc = async (docId) => {
    try {
      await deleteContainerDocument(detail.id, docId);
      setDocList(l => l.filter(d => d.doc_id !== docId));
      toast.success('Removed');
    } catch (e) { toast.error('Remove failed'); }
  };

  const openPutaway = async () => {
    setPutawayOpen(true);
    try {
      const { data } = await getPutawayPlan(detail.id);
      setPutaway(data);
    } catch (e) {
      toast.error('Failed to load plan');
    }
  };

  const handleExecutePutaway = async () => {
    if (!detail?.id || !putaway?.plan) return;
    const moves = putaway.plan
      .filter(p => p.suggested_location_id && p.received_qty > 0)
      .map(p => ({
        line_id: p.line_id,
        qty: p.received_qty,
        target_location_id: p.suggested_location_id,
      }));
    if (moves.length === 0) {
      toast.error('No executable moves in plan');
      return;
    }
    setPutawayBusy(true);
    try {
      // Accept 200 (all ok) and 207 (partial) as non-error.
      const { data, status } = await executePutaway(detail.id, moves, { validateStatus: (s) => s === 200 || s === 207 });
      if (status === 207 || data.failed > 0) {
        toast.warning(`${data.executed} moved · ${data.failed} failed — see table for details`);
      } else {
        toast.success(`Putaway executed · ${data.executed} line(s) moved`);
      }
      setPutawayOpen(false);
      setPutaway(null);
      await refreshDetail();
    } catch (e) {
      // 400 with envelope = every move failed → surface the first error
      const env = e?.response?.data;
      if (env?.results?.length) {
        const firstErr = env.results.find(r => !r.ok)?.error || 'unknown';
        toast.error(`All ${env.failed} move(s) failed · ${firstErr}`);
      } else {
        toast.error(e?.response?.data?.detail || 'Execute failed');
      }
    } finally {
      setPutawayBusy(false);
    }
  };

  const openBarcodePdf = () => {
    if (!detail?.id) return;
    window.open(cartonBarcodesPdfUrl(detail.id), '_blank');
  };

  const handlePublishScorecard = async () => {
    setScorecardBusy(true);
    try {
      const { data } = await publishSupplierScorecard(90);
      if (data.skipped) {
        toast(`Skipped: ${data.skipped}`);
      } else {
        toast.success(`Broadcast · ${data.supplier_count} supplier(s)`);
      }
    } catch (e) {
      toast.error('Publish failed');
    } finally {
      setScorecardBusy(false);
    }
  };

  const addLine = () => setFormLines(l => [...l, { sku_code: '', expected_qty: '', cartons: '', units_per_carton: '', unit_cost: '' }]);
  const removeLine = (i) => setFormLines(l => l.filter((_, idx) => idx !== i));
  const setLine = (i, k, v) => setFormLines(l => l.map((ln, idx) => idx === i ? { ...ln, [k]: v } : ln));

  const handleCreate = async () => {
    try {
      if (createMode === 'asn') {
        if (!asnFile) { toast.error('Pick an ASN file'); return; }
        const fd = new FormData();
        fd.append('file', asnFile);
        if (form.container_number) fd.append('container_number', form.container_number);
        if (form.container_type) fd.append('container_type', form.container_type);
        if (form.supplier_name) fd.append('supplier_name', form.supplier_name);
        if (form.origin_port) fd.append('origin_port', form.origin_port);
        if (form.eta) fd.append('eta', form.eta);
        const { data } = await importContainerAsn(fd);
        toast.success(`${data.container_number} · ${data.lines.length} line(s) imported`);
      } else {
        const cleaned = formLines
          .filter(l => l.sku_code && parseInt(l.expected_qty, 10) > 0)
          .map(l => ({
            sku_code: l.sku_code.trim().toUpperCase(),
            expected_qty: parseInt(l.expected_qty, 10),
            cartons: l.cartons ? parseInt(l.cartons, 10) : null,
            units_per_carton: l.units_per_carton ? parseInt(l.units_per_carton, 10) : null,
            unit_cost: l.unit_cost ? parseFloat(l.unit_cost) : 0,
          }));
        if (cleaned.length === 0) { toast.error('Add at least one valid line'); return; }
        if (!form.container_number) { toast.error('Container number required'); return; }
        // Defensively coerce ETA — bad inputs (typos, partial dates from the picker) used to throw
        // RangeError: Invalid time value at .toISOString() and tear down the page.
        let etaIso = null;
        if (form.eta) {
          const d = new Date(form.eta);
          if (!Number.isNaN(d.getTime())) etaIso = d.toISOString();
          else { toast.error('Invalid ETA — please pick a date again'); return; }
        }
        await createContainer({
          ...form,
          over_receipt_tolerance_pct: parseFloat(form.over_receipt_tolerance_pct) || 5,
          eta: etaIso,
          lines: cleaned,
          freight_cost: parseFloat(form.freight_cost) || 0,
          duty_cost: parseFloat(form.duty_cost) || 0,
          insurance_cost: parseFloat(form.insurance_cost) || 0,
          other_cost: parseFloat(form.other_cost) || 0,
          cost_allocation_method: form.cost_allocation_method || 'value',
        });
        toast.success(`${form.container_number} created`);
      }
      setShowCreate(false);
      setForm({ container_number: '', container_type: '40HC', supplier_name: '', origin_port: '', eta: '', over_receipt_tolerance_pct: 5, blind_receiving: false, freight_cost: '', duty_cost: '', insurance_cost: '', other_cost: '', cost_allocation_method: 'value' });
      setFormLines([{ sku_code: '', expected_qty: '', cartons: '', units_per_carton: '', unit_cost: '' }]);
      setEtaDate(null);  // ensure the date picker doesn't carry over to the next dialog open
      setAsnFile(null);
      refreshAll();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      // FastAPI may return either a string or a list of validation error dicts;
      // toast.error must always receive a string or it throws and unmounts the tree.
      const msg = typeof detail === 'string'
        ? detail
        : Array.isArray(detail)
          ? detail.map((x) => x?.msg || JSON.stringify(x)).join('; ')
          : detail?.msg || e?.message || 'Create failed';
      toast.error(msg);
    }
  };

  const openDetail = async (c) => {
    setDetailLoading(true);
    setDetail({ _loading: true, id: c.id });
    try {
      const { data } = await getContainer(c.id);
      setDetail(data);
    } catch (e) {
      toast.error('Failed to load container');
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleArrive = async () => {
    if (!detail?.id) return;
    try {
      await arriveContainer(detail.id, {});
      toast.success('Marked arrived');
      const { data } = await getContainer(detail.id);
      setDetail(data);
      refreshAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Arrive failed');
    }
  };

  const handleClose = async () => {
    if (!detail?.id) return;
    try {
      const { data } = await closeContainer(detail.id);
      toast.success(`Closed · ${data.shortage_units} short / ${data.overage_units} over`);
      const fresh = await getContainer(detail.id);
      setDetail(fresh.data);
      refreshAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Close failed');
    }
  };

  const submitReceive = async () => {
    if (!detail?.id || !receivingLine) return;
    const n = parseInt(receiveQty, 10);
    if (Number.isNaN(n) || n < 0) { toast.error('Enter a valid qty'); return; }
    try {
      const { data } = await receiveContainerLine(detail.id, {
        line_id: receivingLine.line_id,
        received_qty: n,
      });
      const msg = data.variance_status === 'match' ? '✓ Match' :
                  data.variance_status === 'short' ? `${data.variance} short` :
                  data.variance_status === 'over_tolerance' ? `+${data.variance} OVER TOLERANCE` :
                  data.variance_status === 'over' ? `+${data.variance} over` : 'Received';
      toast.success(msg);
      setReceivingLine(null);
      setReceiveQty('');
      const fresh = await getContainer(detail.id);
      setDetail(fresh.data);
      refreshAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Receive failed');
    }
  };

  const summaryCards = useMemo(() => [
    ['In Transit', dashboard?.in_transit ?? 0, 'bg-blue-50 border-blue-200', 'in_transit'],
    ['Arriving Today', dashboard?.arriving_today ?? 0, 'bg-amber-50 border-amber-200', ''],
    ['Unloading', dashboard?.unloading ?? 0, 'bg-purple-50 border-purple-200', 'unloading'],
    ['Completed Today', dashboard?.completed_today ?? 0, 'bg-emerald-50 border-emerald-200', 'closed'],
    ['Pending Close', dashboard?.pending_close ?? 0, 'bg-rose-50 border-rose-200', 'received'],
  ], [dashboard]);

  return (
    <Layout>
      <div data-testid="containers-page" className="p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
              <ShippingContainer size={32} weight="fill" className="text-brand" />
              Inbound Containers
            </h1>
            <p className="text-muted-foreground">FCL & LCL tracking · ASN, arrival, scan-receiving, variance report.</p>
          </div>
          {isManager && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => setShowPdfImport(true)}
                data-testid="import-po-pdf-btn"
                className="rounded-none border-brand text-brand hover:bg-brand/5"
              >
                <FilePdf size={16} weight="bold" className="mr-2" /> Import PO from PDF
              </Button>
              <Dialog open={showCreate} onOpenChange={setShowCreate}>
                <DialogTrigger asChild>
                  <Button data-testid="new-container-btn" className="bg-brand hover:bg-brand/90 rounded-none">
                    <Plus size={16} weight="bold" className="mr-2" /> New Container
                  </Button>
                </DialogTrigger>
              <DialogContent className="border border-border rounded-none max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="new-container-dialog">
                <DialogHeader>
                  <DialogTitle>New Inbound Container</DialogTitle>
                  <DialogDescription className="text-xs text-muted-foreground">
                    Either fill the form manually or upload an ASN (CSV/JSON).
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="flex gap-2 p-1 bg-muted border border-border">
                    <button
                      type="button"
                      onClick={() => setCreateMode('manual')}
                      data-testid="mode-manual"
                      className={`flex-1 px-3 py-2 text-xs font-bold uppercase tracking-widest ${createMode === 'manual' ? 'bg-brand text-white' : 'text-muted-foreground'}`}
                    >Manual</button>
                    <button
                      type="button"
                      onClick={() => setCreateMode('asn')}
                      data-testid="mode-asn"
                      className={`flex-1 px-3 py-2 text-xs font-bold uppercase tracking-widest ${createMode === 'asn' ? 'bg-brand text-white' : 'text-muted-foreground'}`}
                    >Import ASN</button>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="overline">Container #</Label>
                      <Input value={form.container_number} onChange={(e) => setForm({ ...form, container_number: e.target.value })} placeholder="MSKU1234567" data-testid="container-number-input" />
                    </div>
                    <div>
                      <Label className="overline">Type</Label>
                      <select
                        value={form.container_type}
                        onChange={(e) => setForm({ ...form, container_type: e.target.value })}
                        data-testid="container-type-select"
                        className="w-full h-10 px-3 border border-border bg-card"
                      >
                        {Object.keys(TYPE_LABELS).map(t => (
                          <option key={t} value={t}>{t} · {TYPE_LABELS[t]}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label className="overline">Supplier</Label>
                      <Input value={form.supplier_name} onChange={(e) => setForm({ ...form, supplier_name: e.target.value })} placeholder="Acme Freight" />
                    </div>
                    <div>
                      <Label className="overline">Origin Port</Label>
                      <Input value={form.origin_port} onChange={(e) => setForm({ ...form, origin_port: e.target.value })} placeholder="Shanghai" />
                    </div>
                    <div>
                      <Label className="overline">ETA</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            data-testid="eta-picker"
                            className="w-full justify-start rounded-none border-border font-mono"
                          >
                            <CalendarBlank size={14} className="mr-2" />
                            {etaDate ? format(etaDate, 'PPP') : 'Pick ETA…'}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={etaDate}
                            onSelect={setEtaDate}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div>
                      <Label className="overline">Over-Receipt Tol. %</Label>
                      <Input type="number" value={form.over_receipt_tolerance_pct} onChange={(e) => setForm({ ...form, over_receipt_tolerance_pct: e.target.value })} />
                    </div>
                    <div className="col-span-2 flex items-center gap-3 bg-muted border border-border p-3">
                      <input
                        type="checkbox"
                        id="blind-mode"
                        data-testid="blind-mode-toggle"
                        checked={!!form.blind_receiving}
                        onChange={(e) => setForm({ ...form, blind_receiving: e.target.checked })}
                        className="w-4 h-4"
                      />
                      <label htmlFor="blind-mode" className="text-sm flex-1">
                        <span className="font-bold">Blind receiving</span>
                        <span className="text-muted-foreground ml-2">Hide expected qty from receivers (reduces confirmation bias)</span>
                      </label>
                    </div>
                  </div>

                  {createMode === 'asn' ? (
                    <div>
                      <Label className="overline">ASN File (CSV or JSON)</Label>
                      <Input
                        type="file"
                        accept=".csv,.json"
                        onChange={(e) => setAsnFile(e.target.files?.[0] || null)}
                        data-testid="asn-file-input"
                        className="mt-1"
                      />
                      <p className="text-xs text-muted-foreground mt-2">
                        CSV header: <code>sku_code,description,expected_qty,cartons,units_per_carton,purchase_order_id</code>.
                        JSON matches the container create shape with a <code>lines[]</code> array.
                      </p>
                    </div>
                  ) : (
                    <>
                    <details className="border border-border" data-testid="landed-costs-extras">
                      <summary className="cursor-pointer px-3 py-2 text-xs font-bold uppercase tracking-wider text-muted-foreground hover:text-foreground bg-muted/40">
                        Landed cost extras (optional)
                      </summary>
                      <div className="p-3 space-y-3">
                        <p className="text-[11px] text-muted-foreground">
                          Spread these across the lines below by your chosen method. Updates each SKU's <strong>Landed Cost</strong> (moving weighted average) when you finalize.
                        </p>
                        <div className="grid grid-cols-4 gap-2">
                          <div>
                            <Label className="overline text-[10px]">Freight ($)</Label>
                            <Input type="number" step="0.01" min={0} value={form.freight_cost} onChange={(e) => setForm({ ...form, freight_cost: e.target.value })} data-testid="freight-cost-input" placeholder="0.00" className="font-mono" />
                          </div>
                          <div>
                            <Label className="overline text-[10px]">Duty ($)</Label>
                            <Input type="number" step="0.01" min={0} value={form.duty_cost} onChange={(e) => setForm({ ...form, duty_cost: e.target.value })} data-testid="duty-cost-input" placeholder="0.00" className="font-mono" />
                          </div>
                          <div>
                            <Label className="overline text-[10px]">Insurance ($)</Label>
                            <Input type="number" step="0.01" min={0} value={form.insurance_cost} onChange={(e) => setForm({ ...form, insurance_cost: e.target.value })} data-testid="insurance-cost-input" placeholder="0.00" className="font-mono" />
                          </div>
                          <div>
                            <Label className="overline text-[10px]">Other ($)</Label>
                            <Input type="number" step="0.01" min={0} value={form.other_cost} onChange={(e) => setForm({ ...form, other_cost: e.target.value })} data-testid="other-cost-input" placeholder="0.00" className="font-mono" />
                          </div>
                        </div>
                        <div>
                          <Label className="overline text-[10px]">Allocation method</Label>
                          <select
                            value={form.cost_allocation_method}
                            onChange={(e) => setForm({ ...form, cost_allocation_method: e.target.value })}
                            data-testid="cost-allocation-method"
                            className="w-full mt-1 h-9 px-2 border border-border bg-background text-sm rounded-none"
                          >
                            <option value="value">By value (qty × unit cost) — most accurate for duty</option>
                            <option value="weight">By weight (kg) — best for sea/air freight</option>
                            <option value="volume">By volume (m³) — best for LCL ocean</option>
                            <option value="quantity">By quantity (equal share per unit)</option>
                          </select>
                        </div>
                      </div>
                    </details>
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <Label className="overline">Line Items</Label>
                        <Button type="button" variant="outline" size="sm" onClick={addLine} data-testid="add-line-btn" className="rounded-none">
                          <Plus size={14} className="mr-1" /> Add Line
                        </Button>
                      </div>
                      <div className="border border-border max-h-[280px] overflow-y-auto">
                        <table className="w-full table-dense">
                          <thead className="sticky top-0 bg-card">
                            <tr>
                              <th>SKU Code</th>
                              <th className="text-right">Expected</th>
                              <th className="text-right">Cartons</th>
                              <th className="text-right">U/Carton</th>
                              <th className="text-right" title="Per-unit purchase price (FOB / ex-works) — used as the base of the landed-cost calc">Unit Cost ($)</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {formLines.map((l, i) => (
                              <tr key={i}>
                                <td>
                                  <Input
                                    list="sku-list"
                                    value={l.sku_code}
                                    onChange={(e) => setLine(i, 'sku_code', e.target.value)}
                                    placeholder="SKU-..."
                                    data-testid={`line-sku-${i}`}
                                    className="font-mono"
                                  />
                                </td>
                                <td className="text-right"><Input type="number" value={l.expected_qty} onChange={(e) => setLine(i, 'expected_qty', e.target.value)} data-testid={`line-qty-${i}`} /></td>
                                <td className="text-right"><Input type="number" value={l.cartons} onChange={(e) => setLine(i, 'cartons', e.target.value)} /></td>
                                <td className="text-right"><Input type="number" value={l.units_per_carton} onChange={(e) => setLine(i, 'units_per_carton', e.target.value)} /></td>
                                <td className="text-right"><Input type="number" step="0.01" min={0} value={l.unit_cost} onChange={(e) => setLine(i, 'unit_cost', e.target.value)} data-testid={`line-unit-cost-${i}`} placeholder="0.00" /></td>
                                <td>
                                  {formLines.length > 1 && (
                                    <button onClick={() => removeLine(i)} className="text-muted-foreground hover:text-red-600">
                                      <X size={14} weight="bold" />
                                    </button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        <datalist id="sku-list">
                          {skus.slice(0, 200).map(s => <option key={s.id} value={s.sku_code} label={s.name} />)}
                        </datalist>
                      </div>
                    </div>
                    </>
                  )}

                  <Button
                    onClick={handleCreate}
                    data-testid="submit-container-btn"
                    className="w-full bg-brand hover:bg-brand/90 rounded-none"
                  >
                    {createMode === 'asn' ? <><UploadSimple size={14} className="mr-1" /> Import ASN</> : 'Create Container'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            </div>
          )}
        </div>

        <PoPdfImportDialog
          open={showPdfImport}
          onOpenChange={setShowPdfImport}
          onCreated={() => { refreshAll(); }}
        />

        {/* Dashboard cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-5">
          {summaryCards.map(([label, value, bg, filter]) => (
            <button
              key={label}
              onClick={() => filter && setStatusFilter(filter === statusFilter ? '' : filter)}
              data-testid={`dashboard-${label.toLowerCase().replace(/\s+/g, '-')}`}
              className={`${bg} border p-3 text-left transition ${filter && statusFilter === filter ? 'ring-2 ring-brand' : ''}`}
            >
              <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{label}</div>
              <div className="text-3xl font-bold font-mono">{value}</div>
            </button>
          ))}
        </div>

        {/* Supplier reliability scorecard */}
        <div data-testid="supplier-scorecard-widget" className="bg-card border border-border rounded-lg mb-3">
          <button
            onClick={() => setScorecardOpen(o => !o)}
            className="w-full flex items-center justify-between px-4 py-3 border-b border-border"
          >
            <div className="flex items-center gap-3">
              <div className="bg-blue-50 border border-blue-200 p-2">
                <Trophy size={18} weight="fill" className="text-brand" />
              </div>
              <div className="text-left">
                <p className="overline">Supply-chain</p>
                <h2 className="text-base font-bold tracking-tight">
                  Supplier Reliability (90d)
                </h2>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {scorecard && (
                <span className="text-xs text-muted-foreground">
                  {scorecard.total_suppliers} supplier(s)
                </span>
              )}
              <CaretDown size={14} weight="bold" className={`transition ${scorecardOpen ? '' : '-rotate-90'}`} />
            </div>
          </button>
          {scorecardOpen && (
            <div className="p-4">
              {!scorecard || scorecard.rows.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-6" data-testid="scorecard-empty">
                  No suppliers with both ETA and ATA in the window yet.
                </p>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full table-dense">
                      <thead>
                        <tr>
                          <th>Supplier</th>
                          <th className="text-right">Containers</th>
                          <th className="text-right">On Time</th>
                          <th className="text-right">Late</th>
                          <th className="text-right">Avg Delay (h)</th>
                          <th className="text-right">Reliability</th>
                          <th className="text-right">Grade</th>
                        </tr>
                      </thead>
                      <tbody>
                        {scorecard.rows.map(r => {
                          const gradeStyles = {
                            A: 'bg-emerald-100 text-emerald-800',
                            B: 'bg-blue-100 text-blue-800',
                            C: 'bg-amber-100 text-amber-800',
                            D: 'bg-rose-100 text-rose-800',
                          };
                          return (
                            <tr key={r.supplier} data-testid={`scorecard-row-${r.supplier.replace(/\s+/g, '-')}`}>
                              <td className="font-bold">{r.supplier}</td>
                              <td className="font-mono text-right">{r.total_containers}</td>
                              <td className="font-mono text-right text-emerald-700">{r.on_time}</td>
                              <td className="font-mono text-right text-rose-700">{r.late}</td>
                              <td className="font-mono text-right text-muted-foreground">{r.avg_delay_hours_when_late ?? '—'}</td>
                              <td className="font-mono text-right font-bold">{r.reliability_pct.toFixed(1)}%</td>
                              <td className="text-right">
                                <span className={`px-2 py-1 text-xs font-bold uppercase ${gradeStyles[r.grade]}`}>
                                  {r.grade}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {isManager && (
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-border">
                      <p className="text-xs text-muted-foreground">On-time = ATA ≤ ETA + 24h · Auto-publishes weekly (Tue 10:00 UTC)</p>
                      <Button
                        onClick={handlePublishScorecard}
                        disabled={scorecardBusy}
                        data-testid="scorecard-publish-btn"
                        className="bg-brand hover:bg-brand/90 rounded-none"
                      >
                        <PaperPlaneTilt size={14} weight="bold" className="mr-1" /> Publish now
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Labour KPIs */}
        <div data-testid="labour-kpis-widget" className="bg-card border border-border rounded-lg mb-5">
          <button
            onClick={() => setKpisOpen(o => !o)}
            className="w-full flex items-center justify-between px-4 py-3 border-b border-border"
          >
            <div className="flex items-center gap-3">
              <div className="bg-amber-50 border border-amber-200 p-2">
                <Gauge size={18} weight="fill" className="text-amber-600" />
              </div>
              <div className="text-left">
                <p className="overline">Productivity</p>
                <h2 className="text-base font-bold tracking-tight">
                  Labour KPIs (30d)
                </h2>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {kpis && (
                <span className="text-xs text-muted-foreground">
                  {kpis.receivers.length} receiver(s) · {kpis.containers.length} containers
                </span>
              )}
              <CaretDown size={14} weight="bold" className={`transition ${kpisOpen ? '' : '-rotate-90'}`} />
            </div>
          </button>
          {kpisOpen && (
            <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="overline mb-2">Top Receivers</p>
                {!kpis || kpis.receivers.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No receipts in window.</p>
                ) : (
                  <table className="w-full table-dense">
                    <thead>
                      <tr><th>Name</th><th className="text-right">Units</th><th className="text-right">u/hr</th></tr>
                    </thead>
                    <tbody>
                      {kpis.receivers.slice(0, 5).map(r => (
                        <tr key={r.user_id} data-testid={`kpi-receiver-${r.user_id}`}>
                          <td><div className="font-bold">{r.name}</div><div className="text-[10px] uppercase text-muted-foreground">{r.role}</div></td>
                          <td className="font-mono text-right">{r.units}</td>
                          <td className="font-mono text-right font-bold text-amber-700">{r.units_per_hour ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
              <div>
                <p className="overline mb-2">Fastest Containers</p>
                {!kpis || kpis.containers.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No unloaded containers in window.</p>
                ) : (
                  <table className="w-full table-dense">
                    <thead>
                      <tr><th>Container</th><th className="text-right">Units</th><th className="text-right">Minutes</th><th className="text-right">u/hr</th></tr>
                    </thead>
                    <tbody>
                      {kpis.containers.slice(0, 5).map(c => (
                        <tr key={c.container_number}>
                          <td><div className="font-mono font-bold text-brand">{c.container_number}</div><div className="text-xs text-muted-foreground">{c.supplier_name}</div></td>
                          <td className="font-mono text-right">{c.total_received}</td>
                          <td className="font-mono text-right">{c.minutes_to_unload}</td>
                          <td className="font-mono text-right font-bold">{c.units_per_hour ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}
        </div>

        {/* List filters — type, supplier, ETA bucket, search by container # */}
        {(() => {
          const suppliers = Array.from(new Set(containers.map(c => c.supplier_name).filter(Boolean))).sort();
          const activeFilters = [typeFilter, supplierFilter, etaFilter, searchTerm].filter(Boolean).length;
          return (
            <div className="bg-card border border-border rounded-lg p-3 mb-4" data-testid="container-filters">
              <div className="flex items-center gap-2 flex-wrap">
                {/* Search bar */}
                <div className="relative flex-1 min-w-[180px]">
                  <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                  <input
                    type="search"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search container number…"
                    data-testid="container-search"
                    aria-label="Search container number"
                    className="w-full h-10 pl-9 pr-3 text-sm bg-background border border-border rounded focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand"
                  />
                </div>
                {/* Type */}
                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  data-testid="filter-type"
                  aria-label="Filter by type"
                  className="h-10 px-3 text-sm bg-background border border-border rounded"
                >
                  <option value="" label="All types"></option>
                  {Object.keys(TYPE_LABELS).map(t => (
                    <option key={t} value={t} label={`${t} · ${TYPE_LABELS[t]}`}></option>
                  ))}
                </select>
                {/* Supplier */}
                <select
                  value={supplierFilter}
                  onChange={(e) => setSupplierFilter(e.target.value)}
                  data-testid="filter-supplier"
                  aria-label="Filter by supplier"
                  className="h-10 px-3 text-sm bg-background border border-border rounded max-w-[180px]"
                >
                  <option value="" label="All suppliers"></option>
                  {suppliers.map(s => <option key={s} value={s} label={s}></option>)}
                </select>
                {/* ETA bucket */}
                <select
                  value={etaFilter}
                  onChange={(e) => setEtaFilter(e.target.value)}
                  data-testid="filter-eta"
                  aria-label="Filter by ETA"
                  className="h-10 px-3 text-sm bg-background border border-border rounded"
                >
                  <option value="" label="Any ETA"></option>
                  <option value="overdue" label="Overdue"></option>
                  <option value="7" label="Next 7 days"></option>
                  <option value="30" label="Next 30 days"></option>
                  <option value="future" label="Later than 30 days"></option>
                  <option value="none" label="No ETA set"></option>
                </select>
                {/* Status — lives in the scorecards above but we surface it here too for one-click clearing */}
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  data-testid="filter-status"
                  aria-label="Filter by status"
                  className="h-10 px-3 text-sm bg-background border border-border rounded"
                >
                  <option value="" label="All statuses"></option>
                  <option value="in_transit" label="In transit"></option>
                  <option value="arrived" label="Arrived"></option>
                  <option value="unloading" label="Unloading"></option>
                  <option value="received" label="Received"></option>
                  <option value="closed" label="Closed"></option>
                </select>
                {activeFilters > 0 && (
                  <button
                    type="button"
                    onClick={clearUrlFilters}
                    data-testid="clear-filters-btn"
                    className="h-10 px-3 text-xs font-bold uppercase tracking-wider text-muted-foreground hover:text-foreground border border-border rounded hover:bg-muted inline-flex items-center gap-1.5"
                  >
                    <X size={12} weight="bold" /> Clear · {activeFilters}
                  </button>
                )}
              </div>
            </div>
          );
        })()}

        {/* List */}
        <div className="bg-card border border-border rounded-lg overflow-x-auto">
          <table className="w-full table-dense">
            <thead>
              <tr>
                <th>Container #</th>
                <th>Type</th>
                <th>Supplier</th>
                <th>Origin</th>
                <th>ETA / ATA</th>
                <th>Status</th>
                <th className="text-right">Lines</th>
                <th className="text-right">Exp / Rcv</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="text-center py-10 text-muted-foreground">Loading…</td></tr>
              ) : filteredContainers.length === 0 ? (
                <tr><td colSpan={9} className="text-center py-10 text-muted-foreground" data-testid="containers-empty">
                  {containers.length === 0
                    ? 'No inbound containers yet.'
                    : 'No containers match the current filters.'}
                </td></tr>
              ) : filteredContainers.map(c => {
                const totalExpected = (c.lines || []).reduce((s, l) => s + (l.expected_qty || 0), 0);
                const totalReceived = (c.lines || []).reduce((s, l) => s + (l.received_qty || 0), 0);
                return (
                  <tr key={c.id} data-testid={`container-row-${c.container_number}`} className="hover:bg-muted">
                    <td className="font-mono font-bold text-brand">{c.container_number}</td>
                    <td className="uppercase text-xs font-bold text-muted-foreground">{c.container_type}</td>
                    <td>{c.supplier_name}</td>
                    <td className="text-xs text-muted-foreground">{c.origin_port || '—'}</td>
                    <td className="text-xs text-muted-foreground">
                      {c.ata ? `A: ${new Date(c.ata).toLocaleDateString()}` : c.eta ? `E: ${new Date(c.eta).toLocaleDateString()}` : '—'}
                    </td>
                    <td>
                      <span className={`px-2 py-1 text-xs font-bold uppercase ${STATUS_STYLES[c.status] || 'bg-muted'}`}>
                        {c.status?.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="font-mono text-right">{(c.lines || []).length}</td>
                    <td className="font-mono text-right">
                      <span className="text-muted-foreground">{totalExpected}</span> / <span className="font-bold">{totalReceived}</span>
                    </td>
                    <td className="text-right">
                      <button
                        onClick={() => openDetail(c)}
                        data-testid={`open-container-${c.container_number}`}
                        className="px-3 py-1 text-xs font-bold uppercase tracking-widest border border-brand text-brand hover:bg-brand hover:text-white transition"
                      >
                        Open <CaretRight size={12} weight="bold" className="inline ml-1" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Detail dialog */}
        <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
          <DialogContent className="border border-border rounded-none max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="container-detail-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Package size={20} weight="fill" className="text-brand" />
                {detail?.container_number || '…'}
                {detail && (
                  <span className={`ml-2 px-2 py-0.5 text-[10px] uppercase font-bold ${STATUS_STYLES[detail.status]}`}>
                    {detail.status?.replace('_', ' ')}
                  </span>
                )}
              </DialogTitle>
              <DialogDescription className="text-xs text-muted-foreground">
                Line-by-line expected vs received totals. Receive updates land stock in INBOUND-STAGING.
              </DialogDescription>
            </DialogHeader>
            {detailLoading || detail?._loading ? (
              <div className="py-12 text-center text-muted-foreground">Loading…</div>
            ) : detail ? (
              <div className="space-y-4">
                {/* Summary strip */}
                <div className="grid grid-cols-4 gap-3">
                  <div className="bg-muted border border-border p-3">
                    <p className="overline">Type · Supplier</p>
                    <p className="font-bold">{detail.container_type} · {detail.supplier_name}</p>
                    <p className="text-xs text-muted-foreground">{detail.origin_port || '—'}</p>
                  </div>
                  <div className="bg-muted border border-border p-3">
                    <p className="overline">Expected</p>
                    <p className="text-2xl font-bold font-mono">{detail.summary?.total_expected || 0}</p>
                  </div>
                  <div className="bg-emerald-50 border border-emerald-200 p-3">
                    <p className="overline">Received</p>
                    <p className="text-2xl font-bold font-mono text-emerald-700" data-testid="detail-received-total">
                      {detail.summary?.total_received || 0}
                    </p>
                  </div>
                  <div className={`border p-3 ${detail.summary?.shortage_units ? 'bg-red-50 border-red-200' : detail.summary?.overage_units ? 'bg-amber-50 border-amber-200' : 'bg-muted border-border'}`}>
                    <p className="overline">{detail.summary?.shortage_units ? 'Short' : detail.summary?.overage_units ? 'Over' : 'Variance'}</p>
                    <p className="text-2xl font-bold font-mono">
                      {detail.summary?.shortage_units > 0
                        ? <><TrendDown size={16} weight="bold" className="inline mr-1 text-red-600" />{detail.summary.shortage_units}</>
                        : detail.summary?.overage_units > 0
                        ? <><TrendUp size={16} weight="bold" className="inline mr-1 text-amber-600" />{detail.summary.overage_units}</>
                        : '—'}
                    </p>
                  </div>
                </div>

                {/* Tools toolbar */}
                <div className="flex flex-wrap items-center gap-2">
                  {detail.blind_receiving && (
                    <span className="px-2 py-1 text-[10px] font-bold uppercase tracking-widest bg-slate-900 text-white" data-testid="blind-mode-badge">
                      BLIND MODE
                    </span>
                  )}
                  {['arrived', 'unloading', 'received'].includes(detail.status) && (
                    <Button
                      onClick={() => setScannerOpen(true)}
                      data-testid="scan-carton-btn"
                      size="sm"
                      className="bg-brand hover:bg-brand/90 rounded-none"
                    >
                      <Barcode size={14} className="mr-1" /> Scan Carton
                    </Button>
                  )}
                  <Button
                    onClick={() => setDamageOpen(true)}
                    data-testid="open-damage-btn"
                    size="sm"
                    variant="outline"
                    className="rounded-none border-rose-300 text-rose-700 hover:bg-rose-50"
                  >
                    <Camera size={14} className="mr-1" /> Log Damage ({(detail.damage_reports || []).length})
                  </Button>
                  {(detail.damage_reports || []).some(r => r.photo_base64) && (
                    <Button
                      onClick={() => setGalleryOpen(true)}
                      data-testid="open-gallery-btn"
                      size="sm"
                      variant="outline"
                      className="rounded-none border-rose-300 text-rose-700 hover:bg-rose-50"
                    >
                      <ImageSquare size={14} className="mr-1" /> Photo Gallery
                    </Button>
                  )}
                  {(detail.lines || []).some(l => (l.cartons_list || []).length > 0) && (
                    <Button
                      onClick={openBarcodePdf}
                      data-testid="print-barcodes-btn"
                      size="sm"
                      variant="outline"
                      className="rounded-none"
                    >
                      <Printer size={14} className="mr-1" /> Print Barcodes
                    </Button>
                  )}
                  <Button
                    onClick={openDocs}
                    data-testid="open-docs-btn"
                    size="sm"
                    variant="outline"
                    className="rounded-none"
                  >
                    <FileText size={14} className="mr-1" /> Documents ({(detail.documents || []).length})
                  </Button>
                  {['received', 'closed'].includes(detail.status) && (
                    <Button
                      onClick={openPutaway}
                      data-testid="open-putaway-btn"
                      size="sm"
                      variant="outline"
                      className="rounded-none border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                    >
                      <TreeStructure size={14} className="mr-1" /> Putaway Plan
                    </Button>
                  )}
                </div>

                {/* Lines */}
                <div className="border border-border overflow-x-auto">
                  <table className="w-full table-dense">
                    <thead>
                      <tr>
                        <th>SKU</th>
                        <th>Description</th>
                        <th className="text-right">Exp</th>
                        <th className="text-right">Rcv</th>
                        <th className="text-right">Variance</th>
                        <th className="text-right">Cartons</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {(detail.lines || []).map(l => {
                        const hideExpected = detail.blind_receiving && ['arrived', 'unloading'].includes(detail.status);
                        return (
                          <tr key={l.line_id} data-testid={`line-row-${l.sku_code}`}>
                            <td className="font-mono text-brand font-bold">{l.sku_code}</td>
                            <td className="text-sm text-muted-foreground truncate max-w-[200px]">{l.description}</td>
                            <td className="font-mono text-right text-muted-foreground">{hideExpected ? '—' : l.expected_qty}</td>
                            <td className="font-mono text-right font-bold">{l.received_qty}</td>
                            <td className={`font-mono text-right font-bold ${hideExpected ? 'text-muted-foreground' : (VARIANCE_STYLES[l.variance_status] || 'text-muted-foreground')}`}>
                              {hideExpected ? 'hidden' : <>{l.variance > 0 ? '+' : ''}{l.variance}<span className="block text-[10px] uppercase font-bold">{l.variance_status?.replace('_', ' ')}</span></>}
                            </td>
                            <td className="font-mono text-right text-muted-foreground text-sm">
                              {l.cartons
                                ? <span data-testid={`carton-progress-${l.sku_code}`}>{l.cartons_scanned || 0}/{l.cartons}</span>
                                : '—'}
                            </td>
                            <td className="text-right">
                              {['arrived', 'unloading', 'received'].includes(detail.status) && (
                                <button
                                  onClick={() => { setReceivingLine(l); setReceiveQty(''); }}
                                  data-testid={`receive-line-${l.sku_code}`}
                                  className="px-3 py-1 text-xs font-bold uppercase tracking-widest border border-emerald-600 text-emerald-700 hover:bg-emerald-600 hover:text-white transition"
                                >
                                  Receive
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {detail.status !== 'closed' && (
                  <ContainerReservationsPanel container={detail} onChanged={refreshDetail} />
                )}

                {/* Actions */}
                {isManager && (
                  <div className="flex items-center justify-end gap-2 pt-2 border-t border-border flex-wrap">
                    <Button
                      onClick={() => setLandedCostFor(detail.id)}
                      data-testid="open-landed-costs-btn"
                      variant="outline"
                      className="rounded-none"
                    >
                      <Coins size={14} weight="duotone" className="mr-1 text-amber-600" />
                      {detail.landed_costs_finalized ? 'View Landed Costs' : 'Calculate Landed Costs'}
                      {detail.landed_costs_finalized && (
                        <span className="ml-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-emerald-600 text-white">finalized</span>
                      )}
                    </Button>
                    {detail.status === 'in_transit' && (
                      <Button onClick={handleArrive} data-testid="arrive-btn" className="bg-amber-600 hover:bg-amber-700 text-white rounded-none">
                        <Truck size={14} className="mr-1" /> Mark Arrived
                      </Button>
                    )}
                    {['received', 'unloading'].includes(detail.status) && (
                      <Button onClick={handleClose} data-testid="close-btn" className="bg-slate-900 hover:bg-slate-800 text-white rounded-none">
                        <ClipboardText size={14} className="mr-1" /> Close & Variance Report
                      </Button>
                    )}
                    {detail.status === 'closed' && detail.summary?.shortage_units > 0 && (
                      <span className="text-xs text-red-600 font-bold flex items-center gap-1">
                        <Warning size={14} weight="fill" /> {detail.summary.shortage_units} units short — logged on variance report
                      </span>
                    )}
                    {detail.status === 'closed' && detail.summary?.shortage_units === 0 && (
                      <span className="text-xs text-emerald-600 font-bold flex items-center gap-1">
                        <Check size={14} weight="fill" /> Clean close — no variance
                      </span>
                    )}
                  </div>
                )}
              </div>
            ) : null}
          </DialogContent>
        </Dialog>

        {/* Landed Cost dialog — preview + finalize */}
        <LandedCostDialog
          open={!!landedCostFor}
          onOpenChange={(o) => !o && setLandedCostFor(null)}
          containerId={landedCostFor}
          onChanged={() => {
            // After finalize/reset/edit, refresh the detail dialog so its
            // summary strip + finalized badge stays in sync.
            if (detail?.id) {
              getContainer(detail.id).then((r) => setDetail(r.data)).catch(() => {});
            }
          }}
        />

        {/* Receive line dialog */}
        <Dialog open={!!receivingLine} onOpenChange={(o) => !o && setReceivingLine(null)}>
          <DialogContent className="border border-border rounded-none max-w-md" data-testid="receive-line-dialog">
            <DialogHeader>
              <DialogTitle>
                Receive · {receivingLine?.sku_code}
              </DialogTitle>
              <DialogDescription className="text-xs text-muted-foreground">
                Each receipt is added to the running total; stock lands in INBOUND-STAGING.
              </DialogDescription>
            </DialogHeader>
            {receivingLine && (
              <div className="space-y-4">
                <div className="bg-muted border border-border p-3 grid grid-cols-3 gap-2 text-sm">
                  <div>
                    <p className="overline">Expected</p>
                    <p className="font-mono font-bold text-xl">{receivingLine.expected_qty}</p>
                  </div>
                  <div>
                    <p className="overline">Already Rcv</p>
                    <p className="font-mono font-bold text-xl">{receivingLine.received_qty}</p>
                  </div>
                  <div>
                    <p className="overline">Remaining</p>
                    <p className="font-mono font-bold text-xl text-amber-700">
                      {Math.max(0, (receivingLine.expected_qty || 0) - (receivingLine.received_qty || 0))}
                    </p>
                  </div>
                </div>
                <div>
                  <Label className="overline">Qty to add to receipt</Label>
                  <Input
                    type="number"
                    inputMode="numeric"
                    min="0"
                    value={receiveQty}
                    onChange={(e) => setReceiveQty(e.target.value)}
                    autoFocus
                    data-testid="receive-qty-input"
                    className="mt-1 text-2xl font-mono h-14"
                  />
                </div>
                <Button
                  onClick={submitReceive}
                  data-testid="submit-receive-btn"
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-none"
                >
                  <Check size={16} weight="bold" className="mr-1" /> Record receipt → staging
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
        {/* Carton barcode scanner */}
        {scannerOpen && (
          <BarcodeScanner
            onScan={handleCartonScan}
            onClose={() => setScannerOpen(false)}
            title="Scan carton barcode"
          />
        )}

        {/* Damage capture dialog */}
        <Dialog open={damageOpen} onOpenChange={setDamageOpen}>
          <DialogContent className="border border-border rounded-none max-w-md" data-testid="damage-dialog">
            <DialogHeader>
              <DialogTitle>Log Damage</DialogTitle>
              <DialogDescription className="text-xs text-muted-foreground">
                Flags the carton + persists a damage record with optional photo.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="overline">Reason Code</Label>
                <select
                  value={damageForm.reason_code}
                  onChange={(e) => setDamageForm({ ...damageForm, reason_code: e.target.value })}
                  data-testid="damage-reason-select"
                  className="w-full h-10 px-3 border border-border bg-card"
                >
                  <option value="DAMAGED">Damaged in transit</option>
                  <option value="WET">Water damage</option>
                  <option value="CRUSHED">Crushed</option>
                  <option value="OPEN">Seal broken / open</option>
                  <option value="INFESTED">Infested / contaminated</option>
                  <option value="OTHER">Other</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="overline">Carton ID (optional)</Label>
                  <Input
                    value={damageForm.carton_id}
                    onChange={(e) => setDamageForm({ ...damageForm, carton_id: e.target.value })}
                    placeholder="CTN-…"
                    data-testid="damage-carton-input"
                  />
                </div>
                <div>
                  <Label className="overline">Qty affected</Label>
                  <Input type="number" min="0" value={damageForm.qty} onChange={(e) => setDamageForm({ ...damageForm, qty: parseInt(e.target.value || '0', 10) })} />
                </div>
              </div>
              <div>
                <Label className="overline">Description</Label>
                <Input value={damageForm.description} onChange={(e) => setDamageForm({ ...damageForm, description: e.target.value })} placeholder="Top carton crushed, 5 units ruined" />
              </div>
              <div>
                <Label className="overline">Photo (optional, ≤5MB)</Label>
                <Input type="file" accept="image/*" capture="environment" onChange={handlePhotoChange} data-testid="damage-photo-input" />
                {damageForm.photo_base64 && (
                  <img src={damageForm.photo_base64} alt="preview" className="mt-2 max-h-32 border border-border" />
                )}
              </div>
              <Button onClick={submitDamage} data-testid="submit-damage-btn" className="w-full bg-rose-600 hover:bg-rose-700 text-white rounded-none">
                <Camera size={14} className="mr-1" /> Log Damage
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Documents dialog */}
        <Dialog open={docsOpen} onOpenChange={setDocsOpen}>
          <DialogContent className="border border-border rounded-none max-w-xl" data-testid="documents-dialog">
            <DialogHeader>
              <DialogTitle>Documents · {detail?.container_number}</DialogTitle>
              <DialogDescription className="text-xs text-muted-foreground">
                Packing list · Bill of lading · Commercial invoice · PDF/image, 5MB max.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              {isManager && (
                <div className="grid grid-cols-2 gap-2">
                  {[
                    ['packing_list', 'Packing list'],
                    ['bol', 'Bill of Lading'],
                    ['invoice', 'Invoice'],
                    ['other', 'Other'],
                  ].map(([key, label]) => (
                    <label key={key} className="border border-dashed border-border p-3 text-center cursor-pointer hover:border-brand hover:bg-blue-50 transition">
                      <UploadSimple size={16} className="inline mr-1" /> {label}
                      <input
                        type="file"
                        className="hidden"
                        accept=".pdf,image/*"
                        onChange={(e) => handleDocUpload(e, key)}
                        data-testid={`upload-${key}`}
                      />
                    </label>
                  ))}
                </div>
              )}
              <div className="border border-border max-h-[320px] overflow-y-auto">
                {docList.length === 0 ? (
                  <p className="p-4 text-center text-muted-foreground text-sm" data-testid="docs-empty">No documents yet.</p>
                ) : (
                  <table className="w-full table-dense">
                    <thead><tr><th>Type</th><th>Filename</th><th className="text-right">Size</th><th></th></tr></thead>
                    <tbody>
                      {docList.map(d => (
                        <tr key={d.doc_id} data-testid={`doc-row-${d.doc_id}`}>
                          <td className="uppercase text-xs font-bold text-muted-foreground">{d.doc_type}</td>
                          <td className="text-sm">{d.filename}</td>
                          <td className="font-mono text-xs text-right text-muted-foreground">
                            {Math.round((d.size_bytes || 0) / 1024)}KB
                          </td>
                          <td className="text-right">
                            <button onClick={() => viewDoc(d.doc_id)} className="text-brand text-xs font-bold mr-2" data-testid={`view-doc-${d.doc_id}`}>
                              <Eye size={12} weight="bold" className="inline" />
                            </button>
                            {isManager && (
                              <button onClick={() => removeDoc(d.doc_id)} className="text-rose-600 text-xs font-bold" data-testid={`delete-doc-${d.doc_id}`}>
                                <X size={12} weight="bold" className="inline" />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Putaway plan dialog */}
        <Dialog open={putawayOpen} onOpenChange={setPutawayOpen}>
          <DialogContent className="border border-border rounded-none max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="putaway-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <TreeStructure size={20} weight="fill" className="text-emerald-600" /> Directed Putaway · {detail?.container_number}
              </DialogTitle>
              <DialogDescription className="text-xs text-muted-foreground">
                Suggested final bins based on 60-day velocity + existing bin reuse.
              </DialogDescription>
            </DialogHeader>
            {!putaway ? (
              <div className="py-8 text-center text-muted-foreground">Loading…</div>
            ) : (
              <>
                <table className="w-full table-dense">
                  <thead>
                    <tr>
                      <th>SKU</th>
                      <th className="text-right">Received</th>
                      <th className="text-right">Velocity</th>
                      <th>Suggested Bin</th>
                      <th>Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(putaway.plan || []).map(p => (
                      <tr key={p.line_id} data-testid={`putaway-row-${p.sku_code}`}>
                        <td className="font-mono text-brand font-bold">{p.sku_code}</td>
                        <td className="font-mono text-right">{p.received_qty}</td>
                        <td className="text-right">
                          <span className={`px-2 py-0.5 text-xs font-bold ${
                            p.velocity_class === 'A' ? 'bg-emerald-100 text-emerald-800' :
                            p.velocity_class === 'B' ? 'bg-blue-100 text-blue-800' :
                            p.velocity_class === 'C' ? 'bg-amber-100 text-amber-800' :
                            'bg-muted text-foreground'
                          }`}>{p.velocity_class}</span>
                          <span className="block text-[10px] text-muted-foreground font-mono">{p.consumption_last_60d} consumed</span>
                        </td>
                        <td className="font-mono text-sm">
                          {p.suggested_location_code || <span className="text-muted-foreground">— none found</span>}
                        </td>
                        <td className="text-xs text-muted-foreground">{p.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {isManager && (putaway.plan || []).some(p => p.suggested_location_id && p.received_qty > 0) && (
                  <div className="flex items-center justify-between mt-4 pt-3 border-t border-border">
                    <p className="text-xs text-muted-foreground">
                      Executes all suggested moves atomically — decrements INBOUND-STAGING and
                      increments each target bin with a <code>putaway</code> movement.
                    </p>
                    <Button
                      onClick={handleExecutePutaway}
                      disabled={putawayBusy}
                      data-testid="execute-putaway-btn"
                      className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-none"
                    >
                      <Lightning size={14} weight="bold" className="mr-1" />
                      {putawayBusy ? 'Executing…' : 'Execute all'}
                    </Button>
                  </div>
                )}
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Damage photo gallery */}
        <Dialog open={galleryOpen} onOpenChange={setGalleryOpen}>
          <DialogContent className="border border-border rounded-none max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="gallery-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ImageSquare size={20} weight="fill" className="text-rose-600" />
                Damage Photos · {detail?.container_number}
              </DialogTitle>
              <DialogDescription className="text-xs text-muted-foreground">
                Every photo captured across damage reports for this container.
              </DialogDescription>
            </DialogHeader>
            {(() => {
              const photos = (detail?.damage_reports || []).filter(r => r.photo_base64);
              if (photos.length === 0) {
                return (
                  <p className="py-12 text-center text-muted-foreground text-sm" data-testid="gallery-empty">
                    No photos captured yet.
                  </p>
                );
              }
              return (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {photos.map(r => (
                    <div key={r.report_id} className="border border-border bg-muted" data-testid={`gallery-item-${r.report_id}`}>
                      <img
                        src={r.photo_base64}
                        alt={r.reason_code}
                        className="w-full h-48 object-cover border-b border-border cursor-pointer"
                        onClick={() => {
                          const w = window.open('', '_blank');
                          if (w) w.document.write(`<img src="${r.photo_base64}" style="max-width:100%" />`);
                        }}
                      />
                      <div className="p-2">
                        <p className="text-[10px] uppercase font-bold tracking-widest text-rose-700">
                          {r.reason_code}
                        </p>
                        {r.carton_id && (
                          <p className="font-mono text-xs text-muted-foreground">{r.carton_id}</p>
                        )}
                        {r.description && (
                          <p className="text-xs text-muted-foreground truncate">{r.description}</p>
                        )}
                        <p className="text-[10px] text-muted-foreground font-mono mt-1">
                          {new Date(r.reported_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
